import { FLOSS, DMC_SKEIN_METERS } from '../data/dmc';
import type { ColorIndex, Design, PaletteEntry, Sizing } from './types';
import { simulateNormal } from './normal';
import { simulateAlpha } from './alpha';
import { simulateChain, chainLengthCm } from './chain';

/**
 * Thread arithmetic.
 *
 * Every number here is shown to the user as a worked sum, never as a bare
 * figure, because a crafter who can see the arithmetic can sanity-check it
 * against her own experience. A wrong number that shows its working gets
 * caught; a wrong number on its own costs an evening.
 *
 * ── The load-bearing constant ───────────────────────────────────────────────
 * CM_PER_KNOT = 1.0 cm of working thread consumed per complete double
 * half-hitch knot. Two independent derivations agree:
 *
 *   1. Back-derived from the published halokiwi string-length formula
 *      (braceletbook tutorial #173: length_cm = 45 + P, where P is the
 *      percentage of rows in which the string does the knotting, calibrated
 *      for a 14 cm body with 8 cm ties). A string that knots in every row of
 *      a 14 cm body ties one knot per row; at the default 6.5 rows/cm that is
 *      91 knots for the 100 cm of knot allowance => 1.10 cm/knot. At 7
 *      rows/cm it is 98 knots => 1.02 cm/knot.
 *
 *   2. Straight geometry. A complete knot wraps the working thread twice
 *      around a 6-strand DMC bundle of roughly 1.5 mm diameter:
 *      2 x pi x 1.5 mm = 9.4 mm => 0.94 cm/knot. This is a LOWER bound: the
 *      wrap actually goes around the anchor thread plus the working thread's
 *      own thickness, so the true figure is somewhat larger.
 *
 * The two derivations bracket the answer between about 0.94 and 1.10 cm. The
 * larger end is used, because the two failure modes are not symmetric: 10 cm
 * of extra thread costs nothing and gets trimmed off, while 10 cm too little
 * means unpicking the bracelet or splicing mid-row.
 *
 * NOTE: several craft sources quote 3-4 mm per knot for alpha leaders. That
 * figure is roughly a third of both derivations above and would leave a
 * crafter badly short of thread, so it is deliberately not used. See the
 * README for the full argument.
 */
export const CM_PER_KNOT = 1.1;

/**
 * Twisting or braiding a tie shortens it: you need more thread than the
 * finished tail length. 12.5 cm of thread yields about 8 cm of twisted tie,
 * per the same halokiwi calibration.
 */
export const TIE_TWIST_FACTOR = 12.5 / 8;

/** Slack for handling, the starting knot, and simple human error. */
export const HANDLING_SLACK_CM = 6;

/**
 * Knot gauge: how many rows of knots make one centimetre of bracelet.
 *
 * This is the single most tension-dependent number in the whole app, and craft
 * sources disagree (published figures range from about 5 to 7 rows/cm). The
 * app therefore treats it as an adjustable setting and tells the user to knot
 * a test sample and measure. The defaults below are the middle of the
 * published range.
 */
export interface Gauge {
  /** Rows of diagonal knots per cm of finished length. */
  normalRowsPerCm: number;
  /** Rows of alpha knots per cm. Alpha rows sit slightly tighter. */
  alphaRowsPerCm: number;
  /** Width contributed by each string, in mm. */
  mmPerString: number;
}

export const DEFAULT_GAUGE: Gauge = {
  normalRowsPerCm: 6.5,
  alphaRowsPerCm: 7,
  mmPerString: 1.5,
};

/* -------------------------------------------------------------------------- */

/** One line of shown arithmetic. */
export interface CalcLine {
  label: string;
  /** The sum exactly as it should be printed, e.g. "49 knots x 1.0 cm". */
  expression: string;
  /** Value contributed by this line, in cm. */
  cm: number;
}

export type ThreadRole =
  | 'strand'
  | 'alpha-base'
  | 'alpha-leader'
  | 'chain-core'
  | 'chain-worker';

export interface ThreadCut {
  color: ColorIndex;
  /** DMC details, copied so a serialised design is self-describing. */
  floss: PaletteEntry;
  role: ThreadRole;
  /** Human label, e.g. "String 3 (leftmost)" or "Leader". */
  label: string;
  /** How many pieces of this exact length to cut. */
  pieces: number;
  /** Final recommended cut length, rounded up, in cm. */
  cutCm: number;
  cutIn: number;
  /** The arithmetic, in order, that produced cutCm (before rounding). */
  lines: CalcLine[];
  /** Total before the round-up, so the round-up itself is visible. */
  rawCm: number;
  knotsTied: number;
  /**
   * 'derived' = follows a published, cross-checked formula.
   * 'estimate' = reasoned but not verified against a real bracelet.
   */
  confidence: 'derived' | 'estimate';
  note?: string;
}

export interface MaterialsPlan {
  cuts: ThreadCut[];
  /** Total floss per colour, in cm, across all pieces. */
  perColorTotalCm: { color: ColorIndex; floss: PaletteEntry; cm: number; skeins: number }[];
  bodyLengthCm: number;
  rowCount: number;
  totalKnots: number;
  widthMm: number;
  /** Warnings the crafter should read before cutting. */
  warnings: string[];
}

/* -------------------------------------------------------------------------- */

/** Finished knotted length the bracelet needs to be, before ties. */
export function bodyLengthCm(sizing: Sizing): number {
  return round1(sizing.wristCm + sizing.easeCm);
}

/** Thread reserved at each end for the tie/braid. */
export function tieFinishedCm(sizing: Sizing): number {
  // A loop-and-tie closure needs a longer tail at the tie end, but the loop
  // end is folded thread rather than a tail, so the totals come out similar.
  return sizing.closure === 'loop-and-tie' ? 7 : 6;
}

export function rowsForLength(lengthCm: number, rowsPerCm: number): number {
  return Math.max(2, Math.round(lengthCm * rowsPerCm));
}

/* -------------------------------------------------------------------------- */

export function planMaterials(design: Design, gauge: Gauge = DEFAULT_GAUGE): MaterialsPlan {
  switch (design.body.style) {
    case 'normal':
      return planNormal(design, gauge);
    case 'alpha':
      return planAlpha(design, gauge);
    case 'chain':
      return planChain(design, gauge);
  }
}

function baseLines(body: number, knots: number, tie: number): CalcLine[] {
  return [
    {
      label: 'Bracelet body + handling slack',
      expression: `${fmt(body)} cm body + ${HANDLING_SLACK_CM} cm slack`,
      cm: body + HANDLING_SLACK_CM,
    },
    {
      label: 'Thread eaten by the knots',
      expression: `${knots} knot${knots === 1 ? '' : 's'} x ${fmt(CM_PER_KNOT)} cm`,
      cm: knots * CM_PER_KNOT,
    },
    {
      label: 'Ties at both ends',
      expression: `2 ends x ${fmt(tie)} cm x ${fmt(TIE_TWIST_FACTOR)} (twisting take-up)`,
      cm: 2 * tie * TIE_TWIST_FACTOR,
    },
  ];
}

/**
 * The length of body the thread plan should pay for.
 *
 * This is deliberately taken from the PATTERN, not from the requested wrist
 * size. Patterns are rounded to whole repeats when they are generated, so the
 * band that actually gets knotted can be a couple of centimetres longer or
 * shorter than the request. Cutting thread for the requested length in that
 * case leaves the last rows short — the exact failure this app exists to
 * prevent. The requested size is an input to generation; the pattern is the
 * source of truth for everything downstream of it.
 */
function realisedBodyCm(rows: number, rowsPerCm: number): number {
  return round1(rows / rowsPerCm);
}

function planNormal(design: Design, gauge: Gauge): MaterialsPlan {
  const body = design.body;
  if (body.style !== 'normal') throw new Error('planNormal called with wrong style');
  const sim = simulateNormal(body);
  const bodyCm = realisedBodyCm(sim.rows.length, gauge.normalRowsPerCm);
  const tie = tieFinishedCm(design.sizing);

  const cuts: ThreadCut[] = sim.knotsPerStrand.map((knots, strandId) => {
    const lines = baseLines(bodyCm, sim.hitchPairsPerStrand[strandId]!, tie);
    const raw = sum(lines);
    const colorIdx = body.strandColors[strandId]!;
    return {
      color: colorIdx,
      floss: design.palette[colorIdx]!,
      role: 'strand' as const,
      label: `String ${strandId + 1}${positionNote(strandId, sim.strandCount)}`,
      pieces: 1,
      cutCm: roundUpTo5(raw),
      cutIn: cmToIn(roundUpTo5(raw)),
      lines,
      rawCm: raw,
      knotsTied: knots,
      confidence: 'derived' as const,
    };
  });

  const widthMm = sim.strandCount * gauge.mmPerString;
  const warnings: string[] = [];
  const spread = Math.max(...sim.knotsPerStrand) - Math.min(...sim.knotsPerStrand);
  if (spread > sim.rows.length * 0.4) {
    warnings.push(
      'Some strings do far more knotting than others in this pattern, so their cut lengths differ a lot. Cut each string to its own listed length rather than cutting them all the same.',
    );
  }

  return {
    cuts,
    perColorTotalCm: totalPerColor(cuts, design.palette),
    bodyLengthCm: bodyCm,
    rowCount: sim.rows.length,
    totalKnots: sim.totalKnots,
    widthMm,
    warnings,
  };
}

function planAlpha(design: Design, gauge: Gauge): MaterialsPlan {
  const body = design.body;
  if (body.style !== 'alpha') throw new Error('planAlpha called with wrong style');
  const sim = simulateAlpha(body);
  const bodyCm = realisedBodyCm(sim.rows.length, gauge.alphaRowsPerCm);
  const tie = tieFinishedCm(design.sizing);

  const cuts: ThreadCut[] = [];

  // Base strings barely move: they are knotted over, and only do the knotting
  // where their own colour shows.
  sim.baseKnots.forEach((knots, col) => {
    const lines = baseLines(bodyCm, knots, tie);
    const raw = sum(lines);
    const colorIdx = body.baseColors[col]!;
    cuts.push({
      color: colorIdx,
      floss: design.palette[colorIdx]!,
      role: 'alpha-base',
      label: `Base string ${col + 1}`,
      pieces: 1,
      cutCm: roundUpTo5(raw),
      cutIn: cmToIn(roundUpTo5(raw)),
      lines,
      rawCm: raw,
      knotsTied: knots,
      confidence: 'derived',
    });
  });

  // The leader does nearly all the work and is by far the longest cut.
  const leaderColorCounts = new Map<ColorIndex, number>();
  for (const row of sim.rows) {
    const n = row.knots.filter((k) => k.actor === 'leader').length;
    leaderColorCounts.set(row.leaderColor, (leaderColorCounts.get(row.leaderColor) ?? 0) + n);
  }

  for (const [colorIdx, knots] of [...leaderColorCounts.entries()].sort((a, b) => a[0] - b[0])) {
    // The leader also has to reach across the bracelet on every row, then turn
    // around and come back, which the per-knot figure alone does not cover.
    const traverseCm = (sim.rows.length * sim.cols * gauge.mmPerString) / 10;
    const lines: CalcLine[] = [
      {
        label: 'Bracelet body + handling slack',
        expression: `${fmt(bodyCm)} cm body + ${HANDLING_SLACK_CM} cm slack`,
        cm: bodyCm + HANDLING_SLACK_CM,
      },
      {
        label: 'Thread eaten by the knots',
        expression: `${knots} knots x ${fmt(CM_PER_KNOT)} cm`,
        cm: knots * CM_PER_KNOT,
      },
      {
        label: 'Travelling side to side across every row',
        expression: `${sim.rows.length} rows x ${sim.cols} strings x ${fmt(gauge.mmPerString)} mm`,
        cm: traverseCm,
      },
      {
        label: 'Ties at both ends',
        expression: `2 ends x ${fmt(tie)} cm x ${fmt(TIE_TWIST_FACTOR)} (twisting take-up)`,
        cm: 2 * tie * TIE_TWIST_FACTOR,
      },
    ];
    const raw = sum(lines);
    const split = splitIntoWorkablePieces(raw);
    cuts.push({
      color: colorIdx,
      floss: design.palette[colorIdx]!,
      role: 'alpha-leader',
      label:
        split.pieces === 1 ? 'Leader string (the long one)' : `Leader string — ${split.pieces} pieces, joined as you go`,
      pieces: split.pieces,
      cutCm: split.eachCm,
      cutIn: cmToIn(split.eachCm),
      lines,
      rawCm: raw,
      knotsTied: knots,
      confidence: 'estimate',
      note:
        split.pieces === 1
          ? 'The leader is the thread that runs out. If it is awkwardly long to work with, cut it shorter and join a new piece on with a square knot at the back of the work when you run low — that is normal practice, not a mistake.'
          : `The leader needs about ${fmt(raw / 100)} m in total, which is far too long to work as one thread — it would tangle. ` +
            `Cut ${split.pieces} pieces of ${split.eachCm} cm instead and join the next one on with a square knot at the BACK of the work when you run low. ` +
            `Each piece already includes ${JOIN_ALLOWANCE_CM} cm for the join. Joining the leader is completely normal in alpha bracelets.`,
    });
  }

  const warnings: string[] = [];
  const leaderTotal = Math.max(...cuts.map((c) => c.cutCm * c.pieces));
  if (leaderTotal > DMC_SKEIN_METERS * 100) {
    warnings.push(
      `The leader colour needs about ${fmt(leaderTotal / 100)} m all told, and one standard DMC skein is only ${DMC_SKEIN_METERS} m. Buy ${Math.ceil(leaderTotal / (DMC_SKEIN_METERS * 100))} skeins of it. Alpha bracelets eat background thread — every row is a full row of knots.`,
    );
  }
  warnings.push(
    'Leader length is an estimate, not a guarantee. Joining extra leader thread mid-bracelet is standard practice, so err on the generous side.',
  );

  return {
    cuts,
    perColorTotalCm: totalPerColor(cuts, design.palette),
    bodyLengthCm: bodyCm,
    rowCount: sim.rows.length,
    totalKnots: sim.totalKnots,
    widthMm: sim.cols * gauge.mmPerString,
    warnings,
  };
}

function planChain(design: Design, gauge: Gauge): MaterialsPlan {
  const body = design.body;
  if (body.style !== 'chain') throw new Error('planChain called with wrong style');
  const sim = simulateChain(body);
  // Chain bracelets are measured by their modules, not by rows of knots, so the
  // realised length comes from chainLengthCm rather than a row/gauge division.
  const bodyCm = round1(chainLengthCm(body, gauge.normalRowsPerCm));
  const tie = tieFinishedCm(design.sizing);

  const cuts: ThreadCut[] = sim.strands.map((s) => {
    const lines = baseLines(bodyCm, s.hitchPairs, tie);
    const raw = sum(lines);
    return {
      color: s.color,
      floss: design.palette[s.color]!,
      role: s.role === 'chain' ? ('chain-worker' as const) : ('chain-core' as const),
      label: s.label,
      pieces: 1,
      cutCm: roundUpTo5(raw),
      cutIn: cmToIn(roundUpTo5(raw)),
      lines,
      rawCm: raw,
      knotsTied: s.knots,
      confidence: 'estimate',
    };
  });

  return {
    cuts,
    perColorTotalCm: totalPerColor(cuts, design.palette),
    bodyLengthCm: bodyCm,
    rowCount: sim.modules.length,
    totalKnots: sim.totalKnots,
    widthMm: 6 * gauge.mmPerString,
    warnings: [
      'Chain bracelets are worked in modules rather than rows, so the finished length depends heavily on how tightly you pull the chain sections. Measure against the wrist after two or three motifs and add or drop a motif if needed.',
    ],
  };
}

/* -------------------------------------------------------------------------- */

function totalPerColor(cuts: ThreadCut[], palette: PaletteEntry[]) {
  const totals = new Map<ColorIndex, number>();
  for (const c of cuts) {
    totals.set(c.color, (totals.get(c.color) ?? 0) + c.cutCm * c.pieces);
  }
  return [...totals.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([color, cm]) => ({
      color,
      floss: palette[color] ?? { dmc: '?', name: 'Unknown', hex: '#888888' },
      cm,
      skeins: Math.ceil(cm / (DMC_SKEIN_METERS * 100)),
    }));
}

/**
 * The longest single piece of floss that is actually workable.
 *
 * An alpha leader can easily want 12-15 metres of thread. That number is real —
 * every row of an alpha bracelet is a full row of knots, and they add up — but
 * nobody can work a 15 metre thread: it tangles into a ball within minutes.
 * Real makers work in arm-span lengths and join a new piece on at the back when
 * they run low. So the plan hands back "cut N pieces of X cm", not one absurd
 * number she cannot use.
 *
 * 300 cm is about two arm spans: long enough to keep joins infrequent, short
 * enough to pull through a knot without the far end whipping off the table.
 */
export const MAX_WORKABLE_CM = 300;

/** Thread consumed by each square-knot join, across both pieces. */
export const JOIN_ALLOWANCE_CM = 10;

/**
 * Split a total thread requirement into pieces she can actually handle.
 * Returns the number of pieces and the length of each, including an allowance
 * for the joins themselves.
 */
export function splitIntoWorkablePieces(totalCm: number): { pieces: number; eachCm: number } {
  if (totalCm <= MAX_WORKABLE_CM) return { pieces: 1, eachCm: roundUpTo5(totalCm) };
  // Each join costs thread on both sides, and each piece is rounded up, so the
  // piece count has to be solved for rather than assumed: adding the allowance
  // to a naive total/MAX split can push a piece back over the limit.
  let pieces = Math.ceil(totalCm / MAX_WORKABLE_CM);
  let eachCm = roundUpTo5(totalCm / pieces + JOIN_ALLOWANCE_CM);
  while (eachCm > MAX_WORKABLE_CM) {
    pieces += 1;
    eachCm = roundUpTo5(totalCm / pieces + JOIN_ALLOWANCE_CM);
  }
  return { pieces, eachCm };
}

function positionNote(strandId: number, count: number): string {
  if (strandId === 0) return ' (far left)';
  if (strandId === count - 1) return ' (far right)';
  return '';
}

export function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

export function cmToIn(cm: number): number {
  return Math.round((cm / 2.54) * 10) / 10;
}

export function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * Always round the final cut UP. Running out of thread three rows from the end
 * ruins the bracelet; four extra centimetres costs nothing.
 */
export function roundUpTo5(cm: number): number {
  return Math.max(5, Math.ceil(cm / 5) * 5);
}

function sum(lines: CalcLine[]): number {
  return lines.reduce((a, l) => a + l.cm, 0);
}

/** Nicely formatted "cut 95 cm (37.4 in)", or "cut 5 pieces of 300 cm" when split. */
export function describeCut(cut: ThreadCut): string {
  const each = `${fmt(cut.cutCm)} cm (${fmt(cut.cutIn)} in)`;
  return cut.pieces === 1 ? each : `${cut.pieces} pieces of ${each} — ${fmt((cut.cutCm * cut.pieces) / 100)} m in total`;
}

export const PALETTE_SIZE = FLOSS.length;
